import React, { memo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { ServiceCapability, ServiceOffering } from '../../../types/backend';
import CapabilityForm from './CapabilityForm';
import { useCapabilityManagement } from '../hooks/useCapabilityManagement';
import type { ServiceCapabilityCreateRequest, ServiceCapabilityUpdateRequest } from '../services/serviceOfferingsService';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import { RULE_KIND_LABELS, PRIORITY_LABELS, PRIORITY_COLOR } from '../labels';
import { useEventManagementService } from '../../event-management/services/eventManagementService';
import { useCatalogService } from '../../service-administration/services/catalogService';
import type { ServiceComponentSummary } from '../../service-administration/services/catalogService';
import type { RuleTemplateListItem } from '../../../types/frontend';
import { usePromptService } from '../../prompt/services/promptService';
import type { Prompt } from '../../../types/backend';

interface CapabilitiesPanelProps {
  offering: ServiceOffering;
  onOfferingUpdate: (updated: ServiceOffering) => void;
  readonly?: boolean;
}

const CapabilitiesPanel: React.FC<CapabilitiesPanelProps> = memo(({ offering, onOfferingUpdate, readonly = false }) => {
  const { add, edit, remove, error, isWorking } = useCapabilityManagement(offering.id, onOfferingUpdate);
  const [editingCapability, setEditingCapability] = useState<ServiceCapability | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServiceCapability | null>(null);
  const [templates, setTemplates] = useState<RuleTemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [components, setComponents] = useState<ServiceComponentSummary[]>([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  const eventService = useEventManagementService();
  const catalogService = useCatalogService();
  const promptService = usePromptService();

  useEffect(() => {
    if (!showAddForm && !editingCapability) return;
    let cancelled = false;
    setTemplatesLoading(true);
    eventService.getGlobalTemplates().then((result) => {
      if (!cancelled) {
        setTemplates(result);
        setTemplatesLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setTemplatesLoading(false);
    });
    return () => { cancelled = true; };
  }, [showAddForm, editingCapability, eventService]);

  useEffect(() => {
    if (!showAddForm && !editingCapability) return;
    let cancelled = false;
    setComponentsLoading(true);
    catalogService.listServiceComponents().then((result) => {
      if (!cancelled) {
        setComponents(result.products);
        setComponentsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setComponentsLoading(false);
    });
    return () => { cancelled = true; };
  }, [showAddForm, editingCapability, catalogService]);

  useEffect(() => {
    if (!showAddForm && !editingCapability) return;
    let cancelled = false;
    setPromptsLoading(true);
    promptService.listGlobalPrompts().then((result) => {
      if (!cancelled) {
        setPrompts(result);
        setPromptsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setPromptsLoading(false);
    });
    return () => { cancelled = true; };
  }, [showAddForm, editingCapability, promptService]);

  const capabilities = offering.capabilities ?? [];

  const handleAdd = async (data: ServiceCapabilityCreateRequest | ServiceCapabilityUpdateRequest) => {
    await add(data as ServiceCapabilityCreateRequest);
    setShowAddForm(false);
  };

  const handleEdit = async (data: ServiceCapabilityCreateRequest | ServiceCapabilityUpdateRequest) => {
    if (!editingCapability) return;
    await edit(editingCapability.capability_id, data as ServiceCapabilityUpdateRequest);
    setEditingCapability(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await remove(deleteTarget);
    setDeleteTarget(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Capabilities ({capabilities.length})
        </Typography>
        {!readonly && !showAddForm && !editingCapability && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm(true)}
            disabled={isWorking}
          >
            Add Capability
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
      )}

      {showAddForm && (
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>New Capability</Typography>
          <CapabilityForm
            isNew
            saving={isWorking}
            templates={templates}
            templatesLoading={templatesLoading}
            components={components}
            componentsLoading={componentsLoading}
            prompts={prompts}
            promptsLoading={promptsLoading}
            offeringCapabilities={capabilities}
            onSave={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </Box>
      )}

      {capabilities.length === 0 && !showAddForm && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No capabilities configured.{!readonly && ' Add one to get started.'}
        </Typography>
      )}

      <List disablePadding>
        {capabilities.map((cap, idx) => (
          <React.Fragment key={cap.capability_id}>
            {idx > 0 && <Divider />}
            {editingCapability?.capability_id === cap.capability_id ? (
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, my: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Edit Capability</Typography>
                <CapabilityForm
                  capability={cap}
                  isNew={false}
                  saving={isWorking}
                  templates={templates}
                  templatesLoading={templatesLoading}
                  components={components}
                  componentsLoading={componentsLoading}
                  prompts={prompts}
                  promptsLoading={promptsLoading}
                  offeringCapabilities={capabilities}
                  onSave={handleEdit}
                  onCancel={() => setEditingCapability(null)}
                />
              </Box>
            ) : (
              <ListItem
                disablePadding
                sx={{ py: 0.5, pr: readonly ? 0 : 10 }}
                secondaryAction={!readonly && (
                  <Box>
                    <Tooltip title="Edit capability">
                      <IconButton
                        size="small"
                        aria-label="Edit capability"
                        onClick={() => setEditingCapability(cap)}
                        disabled={isWorking}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove capability">
                      <IconButton
                        size="small"
                        aria-label="Remove capability"
                        onClick={() => setDeleteTarget(cap)}
                        disabled={isWorking}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              >
                <Box sx={{ flex: 1, px: 1, py: 0.5 }}>
                  {/* Outcome-first row: display_name headline + behaviour badge + severity word */}
                  <ListItemText
                    primary={cap.display_name}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                        {cap.group && (
                          <Chip label={cap.group} size="small" variant="outlined" />
                        )}
                        {cap.template_ref ? (
                          <>
                            <Chip
                              label={RULE_KIND_LABELS[cap.rule_kind ?? ''] ?? cap.rule_kind}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={PRIORITY_LABELS[cap.default_priority ?? ''] ?? cap.default_priority}
                              size="small"
                              color={PRIORITY_COLOR[cap.default_priority ?? ''] ?? 'default'}
                              variant="outlined"
                            />
                          </>
                        ) : (
                          <Chip label="Infrastructure" size="small" color="secondary" variant="outlined" />
                        )}
                      </Box>
                    }
                    slotProps={{ secondary: { component: 'span' } }}
                  />
                  {/* Description always visible */}
                  {cap.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 0, mt: 0.25 }}>
                      {cap.description}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove Capability?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmText="Remove"
        confirmColor="error"
      >
        Remove &ldquo;{deleteTarget?.display_name}&rdquo; from this offering? This cannot be undone.
      </ConfirmDialog>
    </Box>
  );
});

CapabilitiesPanel.displayName = 'CapabilitiesPanel';

export default CapabilitiesPanel;
